import { z } from "zod";
import { defineStepConfig } from "../../registry/helpers";

/**
 * Workflow step: Refresh BPIDs on pending E844D transactions.
 *
 * Queries all sd_e844d rows with status='pending' and compares their
 * enrichment_data BPIDs against the latest rexel_ecpbpid reference data.
 * Updates any rows where the BPID has changed and logs activities with
 * before/after values.
 */
export const refreshPendingBpidsStep = defineStepConfig({
  type: "refresh_pending_bpids",
  name: "Refresh Pending BPIDs",
  description:
    "Refreshes suppCustShipToBpid and suppCustBillToBpid on pending E844D " +
    "transactions from the latest ECPBPID reference data. Logs BPID_REFRESHED " +
    "activities for changed rows with before/after values.",
  category: "Transformation",
  libraryModule: "ShipAndDebit",
  inputSchema: z.object({
    company: z
      .string()
      .optional()
      .describe(
        "Restrict the refresh to one company code. Wire from the bulk insert " +
          "step's company output so a fanout child refreshes only its own slice. " +
          "Unwired, every child rescans the whole org.",
      ),
    division: z
      .string()
      .optional()
      .describe(
        "Restrict the refresh to one division code. Wire from the bulk insert " +
          "step's division output so a fanout child refreshes only its own slice. " +
          "Unwired, every child rescans the whole org.",
      ),
  }),
  outputSchema: z.object({
    refreshedCount: z
      .number()
      .describe("Number of pending transactions whose BPIDs were updated"),
    unchangedCount: z
      .number()
      .describe("Number of pending transactions with no BPID change"),
    errorCount: z.number().describe("Number of rows that failed during update"),
  }),
  configSchema: z.object({
    batchSize: z
      .number()
      .prefault(500)
      .describe("Number of changed rows to update per DB batch"),
  }),
  emits: [] as const,
  execution: {
    startToCloseTimeout: "30 minutes",
    retry: { maximumAttempts: 2 },
  },
});
